import { useState, useEffect } from 'react';
import logger from '@/utils/logger';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X, Loader2, Package, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function YarnUsageModal({ 
  isOpen, 
  onClose, 
  yarn,
  onUsageLogged
}) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    project_name: '',
    quantity_used: 1,
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadProjects();
      setFormData({
        project_id: '',
        project_name: '',
        quantity_used: 1,
        notes: ''
      });
    }
  }, [isOpen]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(response.data || []);
    } catch (error) {
      logger.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    setFormData(prev => ({
      ...prev,
      project_id: projectId,
      project_name: project?.name || ''
    }));
  };

  const adjustQuantity = (delta) => {
    setFormData(prev => ({
      ...prev,
      quantity_used: Math.max(1, Math.min(yarn?.quantity || 1, prev.quantity_used + delta))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_id) {
      toast.error(isEnglish ? 'Please select a project' : 'Valitse projekti');
      return;
    }

    if (formData.quantity_used > (yarn?.quantity || 0)) {
      toast.error(isEnglish 
        ? `Not enough yarn. Available: ${yarn?.quantity}` 
        : `Ei tarpeeksi lankaa. Saatavilla: ${yarn?.quantity}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/yarns/${yarn.yarn_id}/use`,
        formData,
        { withCredentials: true }
      );
      
      toast.success(isEnglish 
        ? `Logged ${formData.quantity_used} skein(s) used` 
        : `Kirjattu ${formData.quantity_used} kerä(ä) käytetty`);
      
      onUsageLogged?.(response.data);
      onClose();
    } catch (error) {
      logger.error('Failed to log usage:', error);
      const errorMsg = error.response?.data?.detail || (isEnglish ? 'Failed to log usage' : 'Käytön kirjaus epäonnistui');
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold">
                {isEnglish ? 'Mark as Used' : 'Merkitse Käytetyksi'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {yarn?.brand} - {yarn?.color}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-stone-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Available quantity */}
        <div className="mb-6 p-4 rounded-2xl bg-stone-50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {isEnglish ? 'Available quantity' : 'Saatavilla oleva määrä'}
            </span>
            <span className="text-lg font-bold text-primary">
              {yarn?.quantity || 0} {isEnglish ? 'skeins' : 'kerää'}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Project Selection */}
          <div>
            <Label className="mb-2 block">
              {isEnglish ? 'Project' : 'Projekti'} *
            </Label>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : projects.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {isEnglish 
                  ? 'No projects yet. Create a project first.' 
                  : 'Ei vielä projekteja. Luo ensin projekti.'}
              </p>
            ) : (
              <Select value={formData.project_id} onValueChange={handleProjectChange}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={isEnglish ? 'Select a project' : 'Valitse projekti'} />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Quantity Used */}
          <div>
            <Label className="mb-2 block">
              {isEnglish ? 'Quantity to use' : 'Käytettävä määrä'}
            </Label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => adjustQuantity(-1)}
                disabled={formData.quantity_used <= 1}
                className="rounded-full h-10 w-10"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                min="1"
                max={yarn?.quantity || 1}
                value={formData.quantity_used}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  quantity_used: Math.max(1, Math.min(yarn?.quantity || 1, parseInt(e.target.value) || 1))
                }))}
                className="text-center text-lg font-bold rounded-xl w-20"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => adjustQuantity(1)}
                disabled={formData.quantity_used >= (yarn?.quantity || 1)}
                className="rounded-full h-10 w-10"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <span className="text-muted-foreground">
                {isEnglish ? 'skeins' : 'kerää'}
              </span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="mb-2 block">
              {isEnglish ? 'Notes (optional)' : 'Muistiinpanot (valinnainen)'}
            </Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={isEnglish ? 'e.g., Used for sleeves' : 'esim. Käytetty hihoihin'}
              className="rounded-xl min-h-[80px]"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-full"
            >
              {isEnglish ? 'Cancel' : 'Peruuta'}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !formData.project_id}
              className="flex-1 rounded-full bg-primary text-primary-foreground"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                isEnglish ? 'Log Usage' : 'Kirjaa Käyttö'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
